// Shared bills display logic (used by Bills page + Dashboard/AppLayout for badges & reminders)

// Autopay effective status. Two things happen automatically here so the app
// respects both hands-on and hands-off users:
//   • MANUAL pending: if you flip an autopay bill to Pending, we respect it
//     immediately (returns raw 'pending' → Waiting band, counted in
//     Waiting-to-Clear, out of the Actionable/Unpaid total).
//   • AUTO drift: if you leave an autopay bill 'unpaid' and its due date
//     passes, we surface it AS pending. The charge has likely hit, so the
//     bill belongs in Waiting-to-Clear, not the Actionable total. You can
//     always flip it back to Unpaid or up to Paid manually.
//
// The raw `bill.status` is preserved for the visible status pill.
export function effectiveStatus(bill, today = new Date()) {
  const t = new Date(today); t.setHours(0, 0, 0, 0);
  // Only auto-drift when the user has NOT touched this bill's status. Once
  // they've made an explicit choice (via the status pill or a Mark-* action),
  // status_set_by_user goes true on the server and we respect that forever
  // for this instance. Prevents the "I set it to Unpaid but it keeps
  // becoming Pending" bug on autopay bills whose due date has passed.
  if (bill.is_autopay && bill.status === "unpaid" && !bill.status_set_by_user) {
    const due = new Date(bill.due_date + "T00:00:00");
    if (due <= t) return "pending";
  }
  return bill.status;
}

// A bill lives in the "Paid" column when its amount has left (or is scheduled to leave) the account:
//   - status = paid, cleared, or pending
//   - autopay bill whose due date has passed (auto-debited)
// Otherwise it stays in "Unpaid".
// A bill is "Not Cleared" when it's in the Paid column but hasn't been confirmed cleared yet.
export function displayFor(bill, today) {
  const due = new Date(bill.due_date + "T00:00:00");
  const isPastDue = due <= today;
  const inPaidColumn =
    bill.status === "cleared" ||
    bill.status === "paid" ||
    bill.status === "pending" ||
    (bill.is_autopay && isPastDue);
  if (!inPaidColumn) {
    return { col: "unpaid", notCleared: false };
  }
  return { col: "paid", notCleared: bill.status !== "cleared" };
}

// Backend status → dropdown UI status
export function backendToUiStatus(backendStatus) {
  if (backendStatus === "cleared" || backendStatus === "paid") return "Paid";
  if (backendStatus === "pending") return "Pending";
  return "Unpaid";
}

// Dropdown UI status → backend status to write
export function uiToBackendStatus(uiStatus) {
  if (uiStatus === "Paid") return "cleared";
  if (uiStatus === "Pending") return "pending";
  return "unpaid";
}

// Count only bills that are actionable "not cleared" — past-due through the
// end of the next month. Future-period materialized rows should never be
// counted here even if they somehow land in a paid/pending state.
export function countNotCleared(bills, today = new Date()) {
  const t = new Date(today); t.setHours(0, 0, 0, 0);
  // Cutoff: end of the month AFTER today (~30–60 days ahead).
  const cutoff = new Date(t.getFullYear(), t.getMonth() + 2, 0);
  return bills.reduce((n, b) => {
    if (!displayFor(b, t).notCleared) return n;
    const due = new Date(b.due_date + "T00:00:00");
    if (due > cutoff) return n;
    return n + 1;
  }, 0);
}
